<template>
  <div>
    <main-layout
      controlsBg="light"
      :hasControlsBar="false"
      :breadcrumb="getBreadcrumb">
      <div class="container">
        <grid-row  class="margin-top-xl" column-align="center">
          <grid-col col-size="4">
            <div id="header">
              <h1 class="header-title">
                <chevron
                  @click="$router.push({name: 'Settings'})"/>
                {{ $t('user.title') }}
              </h1>
              <div class="header-add-button">
                <router-link to="/user">
                  <span
                    v-tooltip.left.top-start="$t('user.new')">
                    <font-awesome-icon
                      class="header-add-button-icon"
                      :icon="icon.add"/>
                  </span>
                </router-link>
              </div>
            </div>
            <alert
              v-if="alertVisible"
              :message="alertMessage"
              :type="alertType">
            </alert>
          </grid-col>
        </grid-row>
        <grid-row  class="margin-top-xl" column-align="center">
          <grid-col col-size="4">
            <table-component
              ref="userTable"
              :data="fetchData"
              sort-by="name"
              sort-order="asc"
              :show-caption="false"
              class="datatable"
              filter-placeholder=""
              filterNoResults="">
              <table-column
                show="admin"
                :hidden="true"
                :filterable="false"
                data-type="boolean">
              </table-column>
              <table-column
                show="name"
                :label="$t('user.name')"
                :formatter="formatName"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>
              <table-column
                show="enabled"
                :label="$t('user.status')"
                :formatter="formatStatus"
                header-class="table-component__medium__width__column"
                cell-class="text-center">
              </table-column>
              <table-column
                label=""
                :sortable="false"
                :filterable="false"
                header-class="table-component__minimum__width__column"
                cell-class="text-center">
                <template slot-scope="row">
                  <font-awesome-icon
                    v-if="row.enabled && row.superAdmin === false"
                    :id="row"
                    v-on:click="onUpdate(row)"
                    :icon="icon.pencil"
                    class="table-iconset-icon"/>
                  <font-awesome-icon
                    v-else
                    :icon="icon.pencil"
                    class="table-icon-disabled"/>
                </template>
              </table-column>
              <table-column
                label=""
                :sortable="false"
                :filterable="false"
                header-class="table-component__minimum__width__column"
                cell-class="text-center">
                <template slot-scope="row">
                  <font-awesome-icon
                    v-if="row.parentId !== null && row.enabled && row.superAdmin === false"
                    v-on:click="onDelete(row)"
                    :icon="icon.trash"
                    class="table-iconset-icon"/>
                  <font-awesome-icon
                    v-else
                    :icon="icon.trash"
                    class="table-icon-disabled"/>
                </template>
              </table-column>
            </table-component>
          </grid-col>
        </grid-row>
      </div>
      <ok-cancel-modal
        v-if="showConfirmationDeleteModal"
        v-on:ok="onDeleteConfirm"
        v-on:cancel="onDeleteCancel"
        :title="deleteModalMessage"
        :message="deleteModalMessage"
        :okButton="$t('commons.delete')"
        :cancelButton="$t('commons.cancel')">
        <div slot="body">
          <ul>
            <li>
              {{ userToDelete.name }}
            </li>
          </ul>
        </div>
      </ok-cancel-modal>
    </main-layout>
  </div>
</template>

<script>
import MainLayout from '../MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import pagination from '@/components/shared/table/pagination';
import {mapActions, mapGetters} from 'vuex';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import {
  faPencilAlt,
  faTrashAlt
} from '@fortawesome/pro-regular-svg-icons';
import {faPlusCircle} from '@fortawesome/pro-solid-svg-icons';
import TableComponent from '@/components/shared/table/components/TableComponent';
import TableColumn from '@/components/shared/table/components/TableColumn';
import Alert from '@/components/shared/alert/Alert';
import OkCancelModal from '@/components/shared/dialogs/OkCancelModal';
import BtnPill from '@/components/shared/buttons/BtnPill';
import Chevron from '@/components/shared/Chevron';

export default {
  components: {
    MainLayout,
    GridRow,
    GridCol,
    TableComponent,
    TableColumn,
    FontAwesomeIcon,
    OkCancelModal,
    Alert,
    BtnPill,
    Chevron
  },
  data () {
    return {
      alertVisible: false,
      alertMessage: '',
      alertType: '',
      userToDelete: undefined,
      showConfirmationDeleteModal: false,
      deleteModalMessage: '',
      icon: {
        add: faPlusCircle,
        trash: faTrashAlt,
        pencil: faPencilAlt
      },
      tablePageSize: 10,
      tableState: {
        filter: '',
        page: 1,
        sort: {
          order: '',
          fieldName: ''
        }
      },
      breadcrumb: [
        {link: '/settings', nameKey: 'menu.settings'},
        {link: '/user/list', nameKey: 'user.title', disabled: true}
      ]
    };
  },
  watch: {
    getUsers: {
      handler: function () {
        this.$refs.userTable.refresh();
      }
    }
  },
  created () {
    this.retrieveUsers({context: this});

    const alert = this.$route.params.alert;
    if (alert !== undefined) {
      showAlert(this, this.$t(alert), 'success');
    }
  },
  computed: {
    getBreadcrumb () {
      return this.breadcrumb.map(route => {
        route.name = this.$t(route.nameKey);
        return route;
      });
    },
    ...mapGetters([
      'getUsers'
    ])
  },
  methods: {
    fetchData ({ page, filter, sort }) {
      const tablePage = pagination.createPage(
        this.getUsers,
        this.tableState,
        ['name'],
        this.tablePageSize,
        { page, filter, sort });
      this.tableState = tablePage.state;
      return tablePage.result;
    },
    onUpdate (user) {
      this.$router.push({ name: 'User', params: user });
    },
    onDelete (user) {
      this.userToDelete = user;
      this.deleteModalMessage = this.$t('user.deleteModalMessage');
      this.showConfirmationDeleteModal = true;
    },
    onDeleteConfirm () {
      const id = this.userToDelete.id;
      const groupName = this.userToDelete.name;
      this.userToDelete = {};
      this.showConfirmationDeleteModal = false;
      this.deleteUser({ context: this, id: id }).then(() => {
        showAlert(this, this.$t('user.successDelete', {name: groupName}), 'success');
      }, () => {
        showAlert(this, this.$t('user.errorDelete', {name: groupName}), 'error');
      });
    },
    onDeleteCancel () {
      this.userToDelete = {};
      this.showConfirmationDeleteModal = false;
    },
    formatName (value, rowProperties) {
      return rowProperties.name + (rowProperties.admin === true ? ' (admin)' : '');
    },
    formatStatus (value, rowProperties) {
      return rowProperties.enabled ? this.$t('user.enabled') : this.$t('user.disabled');
    },
    ...mapActions([
      'retrieveUsers',
      'deleteUser'
    ])
  }
};

const showAlert = (context, message, type) => {
  context.alertVisible = true;
  context.alertMessage = message;
  context.alertType = type;
  context.showAlert = true;
  setTimeout(() => {
    context.alertVisible = false;
  }, 2000);
};
</script>

<style lang="scss" >
  @import "src/stylesheets/variables";

  #header {
    display: inline-block;
    width: 100%;
    line-height: 1rem;
  }
  .header-title {
    float: left;
  }
  .header-add-button {
    float: right;
  }
  .header-add-button-icon {
    font-size: 3rem;
    color: $secondary;
  }
</style>



// WEBPACK FOOTER //
// src/layouts/user/UserList.vue